import React, { useContext, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { Button, SearchBar, Text } from '@rneui/themed';
import DropDownPicker from 'react-native-dropdown-picker';
import { parseISO, addMinutes, isWithinInterval } from 'date-fns';
import NavSearchIcon from './NavSearchIcon';
import { collectAncillaryOptions } from '../../common/helpers/Navigation';
import AppContext, { AppActions, AppEvents } from '../../contexts/App';
import AuthContext from '../../contexts/Auth';
import { fetchPage } from '../../common/Api';
import useLiveService from '../../hooks/useLiveService';

const Ancillary = ({ nodeTypeAlias = 'reactTopNavigation' }) => {
    const { state, dispatch } = useContext(AppContext);
    const { state: authState, logout } = useContext(AuthContext);
    const nextLiveService = useLiveService();
    const [searchQuery, setSearchQuery] = useState();
    const options = collectAncillaryOptions(Platform.OS.equals('web') ? state.siteWrapper?.web?.contentItems : state.siteWrapper?.app?.contentItems);
    const [openMenu, setOpenMenu] = useState();

    const handleMenuSelection = async e => {
        if (e.internalView) {
            dispatch({
                type: AppActions.NAVIGATE_INTERNAL,
                data: {
                    internalView: e.internalView,
                    path: e.path
                }
            });
        } else if (e.internalAction) {
            await logout();
        } else {
            if (e.isExternal) {
                Linking.openURL(e.value);
            } else {
                await fetchPage(dispatch, e.value);
            }
        }
    };

    const renderLinks = () => {
        const renderWatchLive = () => {
            if (!nextLiveService) {
                return null;
            } else {
                const upcomingDateTime = nextLiveService?.upcomingDateTime;
                const durationMinutes = nextLiveService?.durationMinutes;
                const now = new Date();
                const upcomingDate = typeof upcomingDateTime === 'string' ? parseISO(upcomingDateTime) : new Date(upcomingDateTime);
                const endingDate = addMinutes(upcomingDate, durationMinutes);

                if (!isWithinInterval(now, { start: upcomingDate, end: endingDate })) {
                    return null;
                }
            }

            return (
                <Button
                    containerStyle={Styles.liveButton}
                    buttonStyle={{ backgroundColor: '#a61a2b', padding: 0 }}
                    radius={10}
                    size='sm'
                    title='Live'
                    titleStyle={Styles.linkButtonTitle}
                />
            );
        };

        return options.links.map(link => {
            const handlePress = async () => {
                if (state.search?.event?.equals(AppEvents.SEARCH_QUERY_SUBMITTED)) {
                    dispatch({
                        type: AppActions.SEARCH_STATE_CHANGE,
                        event: AppEvents.SEARCH_QUERY_CLOSED
                    });
                }

                link.isExternal ? Linking.openURL(link.url) : await fetchPage(dispatch, link.url);
            };

            return (
                <View key={link.text} style={{ cursor: 'pointer', flexDirection: 'row', margin: 20 }}>
                    <Text alt={link.text} onPress={handlePress} style={Styles.linkTitle}>
                        {link.text}
                    </Text>
                    {link.text.equals('Watch', false) && renderWatchLive()}
                </View>
            );
        });
    };

    const renderMenus = () => {
        return options.menus.map(menu => {
            const items = menu.entries
                .filter(entry => {
                    if (authState?.user?.loggedIn) {
                        if (entry.text.equals('Login') || entry.text.equals('Register')) {
                            return false;
                        }
                    }

                    return true;
                })
                .map(entry => {
                    return {
                        isExternal: entry.isExternal,
                        label: entry.text,
                        labelStyle: Styles.menuText,
                        internalView: entry.internalView,
                        value: entry.url
                    };
                });

            if (authState?.user?.loggedIn) {
                items.push(
                    {
                        isExternal: false,
                        label: 'Profile',
                        labelStyle: Styles.menuText,
                        internalView: 'Authentication/Profile',
                        path: '/user-profile'
                    },
                    {
                        isExternal: false,
                        label: 'Logout',
                        labelStyle: Styles.menuText,
                        internalAction: 'Authentication/Logout'
                    }
                );
            }

            return (
                <View key={menu.name}>
                    <DropDownPicker
                        closeAfterSelecting={true}
                        items={items}
                        itemKey='label'
                        itemProps={{ style: { padding: 10 } }}
                        labelProps={{ style: Styles.dropdown.label }}
                        placeholder={menu.name}
                        placeholderStyle={Styles.dropdown.placeholder}
                        onSelectItem={item => handleMenuSelection(item)}
                        onOpen={() => setOpenMenu(menu.name)}
                        onPress={() => {
                            if (openMenu === menu.name) {
                                setOpenMenu(undefined);
                            } else {
                                setOpenMenu(menu.name);
                            }
                        }}
                        open={openMenu && openMenu === menu.name}
                        style={StyleSheet.flatten(Styles.dropdown.container, { justifyContent: 'flex-start' })}
                        textColor='#153d6e'
                        flatListProps={{ testID: `ancillary-dropdown-${menu.name}-flatlist` }}
                        dropDownContainerStyle={{ width: 200, top: 50 }}
                    />
                </View>
            );
        });
    };

    if (state?.action?.data?.page) {
        const hasAncillaryNavigation = state.action.data.page?.hasAncillaryNavigation;

        if (!hasAncillaryNavigation) {
            return null;
        }
    }

    return (
        <View dataSet={{ umbracoComponentName: nodeTypeAlias }}>
            <View style={Styles.headerContainer}>
                <View style={Styles.leftContainer}>{renderLinks()}</View>
                <View style={Styles.rightContainer}>
                    <View style={{ alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-start' }}>
                        {renderMenus()}
                        <NavSearchIcon />
                    </View>
                </View>
            </View>
            {state?.search?.state?.equals(AppEvents.SEARCH_DIALOGUE_STATE_OPEN) && (
                <SearchBar
                    containerStyle={{
                        backgroundColor: '#f7f8f9',
                        borderColor: '#dde4e9',
                        borderWidth: 0.25,
                        borderRadius: 5
                    }}
                    placeholder='Search ...'
                    placeholderTextColor='#153d6e'
                    onCancel={() => {
                        dispatch({
                            type: AppActions.SEARCH_STATE_CHANGE,
                            event: AppEvents.SEARCH_QUERY_CLOSED
                        });
                    }}
                    onChangeText={query => setSearchQuery(query)}
                    onSubmitEditing={() => {
                        setSearchQuery('');
                        dispatch({
                            type: AppActions.SEARCH_STATE_CHANGE,
                            event: AppEvents.SEARCH_QUERY_SUBMITTED,
                            data: searchQuery
                        });
                    }}
                    round={true}
                    searchIcon={{
                        color: '#153d6e',
                        name: 'search',
                        size: 20,
                        type: 'material'
                    }}
                    style={Styles.searchText}
                    value={searchQuery}
                />
            )}
        </View>
    );
};

const Styles = StyleSheet.create({
    dropdown: {
        container: {
            borderRadius: 0,
            borderWidth: 0,
            flexDirection: 'row',
            justifyContent: 'center',
            marginRight: 10
        },
        label: {
            fontFamily: 'GothamMedium',
            color: '#153d6e',
            display: 'flex',
            fontSize: '14pt',
            marginBottom: 0,
            marginRight: 10,
            maxWidth: 150,
            lineHeight: '16pt',
            justifyContent: 'flex-end'
        },
        placeholder: {
            fontFamily: 'GothamMedium',
            color: '#153d6e',
            fontSize: '14pt',
            marginBottom: 0,
            lineHeight: '16pt',
            marginRight: 10
        }
    },
    headerContainer: {
        backgroundColor: 'transparent',
        borderBottomColor: '#dde4e9',
        borderBottomWidth: StyleSheet.hairlineWidth,
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    leftContainer: {
        flexDirection: 'row',
        justifyContent: 'space-evenly'
    },
    linkButtonTitle: {
        fontFamily: 'GothamBook',
        color: '#fff',
        fontSize: '10pt',
        marginBottom: 0,
        lineHeight: '12pt'
    },
    linkTitle: {
        fontFamily: 'GothamMedium',
        color: '#153d6e',
        fontSize: '14pt',
        marginBottom: 0,
        lineHeight: '16pt'
    },
    liveButton: {
        alignSelf: 'center',
        backgroundColor: '#a61a2b',
        marginLeft: 5,
        padding: 2,
        position: 'relative'
    },
    menuText: {
        fontFamily: 'GothamBook',
        color: '#153d6e',
        fontSize: '10pt',
        marginBottom: 0,
        lineHeight: '12pt'
    },
    rightContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        marginRight: 10
    },
    searchText: {
        color: '#000',
        fontFamily: 'GothamBook',
        fontSize: '18pt',
        letterSpacing: 0,
        lineHeight: '20pt'
    },
    searchIcon: {
        borderLeftColor: '#dde4e9',
        borderLeftWidth: StyleSheet.hairlineWidth,
        paddingLeft: 5
    }
});

export default Ancillary;
